<template>
  <el-dialog
    v-model="visible"
    :title="dialogTitle"
    width="520px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    :append-to-body="true"
    destroy-on-close
    class="sensitive-approval-apply-dialog"
  >
    <div v-if="dialogPhase === 'confirm'" class="approval-summary">
      <div class="approval-message">{{ approval.message || '当前操作需要审核，请填写申请原因' }}</div>
      <el-descriptions :column="1" border size="small">
        <el-descriptions-item label="操作内容">
          <template v-if="approval.operationNames?.length">
            <el-tag v-for="name in approval.operationNames" :key="name" class="mr-6px" type="warning">
              {{ name }}
            </el-tag>
          </template>
          <span v-else>--</span>
        </el-descriptions-item>
        <el-descriptions-item label="业务对象">
          {{ approvalBusinessName }}
        </el-descriptions-item>
        <el-descriptions-item v-for="row in approvalDetailRows" :key="`${row.label}:${row.value}`" :label="row.label">
          {{ row.value }}
        </el-descriptions-item>
      </el-descriptions>
    </div>

    <el-form v-if="dialogPhase === 'confirm'" ref="formRef" :model="form" :rules="rules" label-position="top">
      <el-form-item label="申请原因" prop="reason">
        <el-input
          v-model.trim="form.reason"
          type="textarea"
          :rows="4"
          :maxlength="200"
          show-word-limit
          placeholder="请输入申请原因"
          :disabled="submitting"
        />
      </el-form-item>
    </el-form>

    <div v-else class="approval-pending">
      <el-result icon="warning" :title="pendingResultTitle" :sub-title="pendingResultSubTitle">
        <template #extra>
          <el-descriptions :column="1" border size="small" class="approval-pending__descriptions">
            <el-descriptions-item label="审核单号">{{ pendingApproval?.ticketId || '--' }}</el-descriptions-item>
            <el-descriptions-item label="状态">待审核</el-descriptions-item>
            <el-descriptions-item label="操作内容">
              <template v-if="pendingApproval?.operationNames?.length">
                <el-tag v-for="name in pendingApproval.operationNames" :key="name" class="mr-6px" type="warning">
                  {{ name }}
                </el-tag>
              </template>
              <span v-else>--</span>
            </el-descriptions-item>
            <el-descriptions-item label="业务对象">
              {{ pendingApprovalBusinessName }}
            </el-descriptions-item>
            <el-descriptions-item
              v-for="row in pendingApprovalDetailRows"
              :key="`${row.label}:${row.value}`"
              :label="row.label"
            >
              {{ row.value }}
            </el-descriptions-item>
            <el-descriptions-item label="申请原因">{{ form.reason || '--' }}</el-descriptions-item>
          </el-descriptions>
        </template>
      </el-result>
      <el-alert
        class="approval-pending__alert"
        type="warning"
        :title="pendingAlertTitle"
        show-icon
        :closable="false"
      />
    </div>

    <el-alert
      v-if="errorMessage"
      class="approval-error"
      type="error"
      :title="errorMessage"
      show-icon
      :closable="false"
    />

    <template #footer>
      <div v-if="dialogPhase === 'confirm'" class="dialog-footer">
        <el-button :disabled="submitting" @click="handleCancel">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleConfirm">提交审核</el-button>
      </div>
      <div v-else class="dialog-footer">
        <el-button @click="openApprovalList">查看审核列表</el-button>
        <el-button type="primary" @click="handleCollapsePending">收起等待</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import type { SensitiveApprovalDetailRow, SensitiveApprovalResponse, SensitiveApprovalWebsocketPayload } from './types'
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useSensitiveApprovalStoreHook } from '@/store/modules/sensitiveApproval'
import { formatApprovalBusinessName } from './approvalBusiness'
import { isSensitiveApprovalDuplicatePendingError } from './approvalDialog'
import { isPendingApproval } from './approvalGuards'
import { openSensitiveApprovalList } from './approvalListRoute'
import {
  dispatchSensitiveApprovalChanged,
  isApprovalTerminalStatus,
  SENSITIVE_APPROVAL_CHANGED_EVENT,
} from './approvalPending'

const props = defineProps<{
  approval: SensitiveApprovalResponse
  submitter: (_reason: string) => Promise<unknown>
}>()

const emit = defineEmits<{
  cancel: []
  resolved: [_result: unknown]
}>()

const visible = ref(true)
const submitting = ref(false)
const errorMessage = ref('')
const formRef = ref<FormInstance>()
const dialogPhase = ref<'confirm' | 'pending'>('confirm')
const pendingApproval = ref<SensitiveApprovalResponse>()
const pendingResult = ref<unknown>()
const duplicatePending = ref(false)
const approvalStore = useSensitiveApprovalStoreHook()
let pendingResolved = false
const form = reactive({
  reason: getEnabledDefaultApplicationReason(props.approval),
})

const pendingDialogTitle = computed(() => isAutoPassedStatus(pendingApproval.value?.status) ? '敏感操作执行状态' : '等待敏感操作审核')
const dialogTitle = computed(() => dialogPhase.value === 'pending' ? pendingDialogTitle.value : '敏感操作审核申请')
const pendingResultTitle = computed(() => {
  if (duplicatePending.value) {
    return '已有待审核申请'
  }
  switch (pendingApproval.value?.status) {
    case 'EXECUTED':
      return '审核已通过并执行完成'
    case 'EXECUTE_FAILED':
      return '审核执行失败'
    case 'APPROVED':
    case 'EXECUTING':
      return '审核已自动通过'
    default:
      return '审核申请已提交'
  }
})
const pendingResultSubTitle = computed(() => {
  if (duplicatePending.value) {
    return '当前业务对象已有待审核申请，本次未重复提交，请等待审核人处理'
  }
  switch (pendingApproval.value?.status) {
    case 'EXECUTED':
      return '原操作已执行完成'
    case 'EXECUTE_FAILED':
      return '审核已通过，但系统自动执行失败，请刷新页面确认业务状态'
    case 'APPROVED':
    case 'EXECUTING':
      return '提交人具备审核权限，系统已自动通过并正在执行原操作'
    default:
      return '当前操作还未完成，请等待审核人处理'
  }
})
const pendingAlertTitle = computed(() => {
  if (duplicatePending.value) {
    return '本次未重复提交新的审核申请，审核完成前请不要重复操作。'
  }
  if (isAutoPassedStatus(pendingApproval.value?.status)) {
    return '系统已开始处理该敏感操作，请稍后刷新确认结果。'
  }
  return '该业务对象正在审核中，审核完成前请不要重复提交同类敏感操作。'
})
const approvalBusinessName = computed(() => formatApprovalBusinessName(props.approval) || '--')
const approvalDetailRows = computed(() => normalizeDetailRows(props.approval.approvalDetailRows))
const pendingApprovalBusinessName = computed(() => {
  return pendingApproval.value ? formatApprovalBusinessName(pendingApproval.value) || '--' : '--'
})
const pendingApprovalDetailRows = computed(() => normalizeDetailRows(pendingApproval.value?.approvalDetailRows || props.approval.approvalDetailRows))

const rules: FormRules = {
  reason: [
    { required: true, message: '请输入申请原因', trigger: 'blur' },
    { max: 200, message: '申请原因不能超过200字', trigger: 'blur' },
  ],
}

function getEnabledDefaultApplicationReason(approval: SensitiveApprovalResponse) {
  if (approval.defaultApplicationReasonEnabled !== true) {
    return ''
  }
  return String(approval.defaultApplicationReason || '').trim()
}

function handleCancel() {
  if (submitting.value) {
    return
  }
  visible.value = false
  emit('cancel')
}

function isAutoPassedStatus(status?: string) {
  return status === 'APPROVED' || status === 'EXECUTING' || status === 'EXECUTED' || status === 'EXECUTE_FAILED'
}

function normalizeDetailRows(rows?: SensitiveApprovalDetailRow[]) {
  const seen = new Set<string>()
  return (rows || [])
    .map(row => ({
      label: String(row?.label || '').trim(),
      value: String(row?.value ?? '').trim(),
    }))
    .filter(row => row.label && row.value && row.value !== '--')
    .filter(row => {
      const key = `${row.label}:${row.value}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

async function handleConfirm() {
  if (!formRef.value) {
    return
  }
  try {
    await formRef.value.validate()
    submitting.value = true
    errorMessage.value = ''
    duplicatePending.value = false
    const result = await props.submitter(form.reason.trim())
    const replayApprovalPayload = extractApprovalPayload(result)
    if (isPendingApproval(replayApprovalPayload)) {
      enterPendingState(replayApprovalPayload, result)
      return
    }
    visible.value = false
    emit('resolved', result)
  } catch (error) {
    if (isSensitiveApprovalDuplicatePendingError(error)) {
      enterPendingState({
        ...props.approval,
        phase: 'PENDING',
        status: 'PENDING',
        message: getErrorMessage(error),
      }, undefined, { duplicate: true })
      return
    }
    errorMessage.value = getErrorMessage(error)
  } finally {
    submitting.value = false
  }
}

function enterPendingState(approval: SensitiveApprovalResponse, result: unknown, options?: { duplicate?: boolean }) {
  pendingApproval.value = approval
  pendingResult.value = result ?? {
    code: 0,
    data: approval,
  }
  duplicatePending.value = Boolean(options?.duplicate)
  approvalStore.upsertFromPayload(approval)
  errorMessage.value = ''
  dialogPhase.value = 'pending'
  dispatchSensitiveApprovalChanged(approval)
}

function getErrorMessage(error: unknown) {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as { msg?: unknown, message?: unknown }
    if (typeof maybeError.msg === 'string' && maybeError.msg) {
      return maybeError.msg
    }
    if (typeof maybeError.message === 'string' && maybeError.message) {
      return maybeError.message
    }
  }
  return '审核申请提交失败，请稍后重试'
}

function handleCollapsePending() {
  approvalStore.showFloatingTicket(pendingApproval.value?.ticketId)
  resolvePending()
}

function resolvePending() {
  if (pendingResolved) {
    return
  }
  pendingResolved = true
  visible.value = false
  emit('resolved', pendingResult.value)
}

function openApprovalList() {
  openSensitiveApprovalList()
  resolvePending()
}

function matchesPendingApproval(payload: SensitiveApprovalWebsocketPayload) {
  const pending = pendingApproval.value
  if (!pending) {
    return false
  }
  return Boolean(
    (pending.ticketId && payload.ticketId === pending.ticketId) ||
    (pending.requestId && payload.requestId === pending.requestId),
  )
}

function handleApprovalChanged(event: Event) {
  const payload = (event as CustomEvent<SensitiveApprovalWebsocketPayload>).detail
  if (
    dialogPhase.value !== 'pending' ||
    !payload ||
    !matchesPendingApproval(payload)
  ) {
    return
  }

  pendingApproval.value = {
    ...pendingApproval.value,
    ...payload,
  }
  approvalStore.upsertFromPayload(payload)

  if (isApprovalTerminalStatus(payload.status)) {
    resolvePending()
  }
}

function extractApprovalPayload(result: unknown) {
  if (result && typeof result === 'object') {
    const record = result as { data?: unknown, code?: unknown }
    if (record.data && typeof record.data === 'object' && 'code' in record.data) {
      return (record.data as { data?: unknown }).data
    }
    if ('code' in record && 'data' in record) {
      return record.data
    }
  }
  return result
}

defineExpose({
  dialogPhase,
  handleApprovalChanged,
  handleCollapsePending,
  handleConfirm,
  pendingApproval,
  pendingResultSubTitle,
  pendingResultTitle,
  visible,
})

onMounted(() => {
  window.addEventListener(SENSITIVE_APPROVAL_CHANGED_EVENT, handleApprovalChanged)
})

onBeforeUnmount(() => {
  window.removeEventListener(SENSITIVE_APPROVAL_CHANGED_EVENT, handleApprovalChanged)
})
</script>

<style scoped>
.approval-summary {
  margin-bottom: 16px;
}

.approval-message {
  margin-bottom: 12px;
  line-height: 1.5;
  color: var(--el-text-color-primary);
}

.approval-error {
  margin-top: 8px;
}

.approval-pending {
  margin-top: -12px;
}

.approval-pending__descriptions {
  width: 100%;
  text-align: left;
}

.approval-pending__alert {
  margin-top: 12px;
}

.dialog-footer {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
</style>
